// Sample Dec'26 — dedicated modal with tabs, horizontal-scroll category library, and drill-down
import { useState, useEffect } from "react";
import {
  Film, Tv, Music, X, ChevronLeft, BookOpenCheck, Sparkles, Crown, Trophy, Baby, Gift,
  Globe, Star, Award, Smile, Camera, HeartPulse, Volume2, Briefcase,
  Coffee, BookOpen, Leaf, Shirt, MapPin, Drama, Mic, Headphones, PartyPopper,
  Radio, Heart, Landmark, Cog
} from "lucide-react";
import { createPortal } from "react-dom";
import { useAirline } from "../context/AirlineContext";

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = "#F2A900";

const tabs = [
  { id: "hollywood", label: "Hollywood Movies", icon: Film, color: "#F2A900" },
  { id: "intl", label: "International Movies", icon: Globe, color: "#3B82F6" },
  { id: "short", label: "Short Features", icon: Tv, color: "#A855F7" },
  { id: "audio", label: "Audio", icon: Music, color: "#10B981" },
];

// Map of category-name → icon component (so each category gets an illustrative icon)
const iconMap = {
  // Hollywood
  "Hollywood Fresh Picks": Sparkles,
  "Evergreen Cinema": Crown,
  "Award Winning Cinema": Trophy,
  "Kids Corner": Baby,
  "Festive Theme": Gift,
  // International
  "Chinese New Onboard": Star,
  "Latest Releases in Japan": MapPin,
  "Just Added in Korea": Heart,
  "Signature Singapore": Landmark,
  "Indian Premieres": Sparkles,
  "Europe Landing": Globe,
  "Must Watch Chinese": Crown,
  "Japenese Hall of Fame": Award,
  "Japanese Hall of Fame": Award,
  "Iconic Korean Flicks": Star,
  // Short Features
  "Bring The Drama": Drama,
  "Global Comedy Hits": Smile,
  "Discover Beyond": Camera,
  "Arrive Elevated": HeartPulse,
  "Anime Collections": Sparkles,
  "Junior Flyers": Baby,
  "Art and Culture Canvas": Volume2,
  "The Sports Club": Trophy,
  "Lifestyle": Coffee,
  "Best of Chinese TV": Tv,
  "Japanese Showcase": Tv,
  "Singaporean Picks": Landmark,
  "Into the K-Drama": Heart,
  "Business": Briefcase,
  "History": BookOpen,
  "Environment & Sustainability": Leaf,
  "Fashion": Shirt,
  "Award Winning TV": Award,
  // Audio
  "Pop": Headphones,
  "R&B / Soul": Music,
  "Hip-Hop / Rap": Mic,
  "Rock": Radio,
  "Electronic / Dance": Sparkles,
  "Jazz": Music,
  "World / Asian": Globe,
  "Classical": Music,
  "Festive / Holiday": PartyPopper,
  "Production Podcasts": Cog,
  "Podcasts": Mic,
};

const iconFor = (name) => iconMap[name] || BookOpenCheck;

const Dec26Modal = ({ isDark, onClose }) => {
  const [activeTab, setActiveTab] = useState("hollywood");
  const [activeCat, setActiveCat] = useState(null); // category object when drilled-down
  const [dec26Data, setDec26Data] = useState(null);
  const { airline } = useAirline();

  useEffect(() => {
    let alive = true;
    const code = airline?.code || "SQ";
    fetch(`${API}/api/pitch/${code}/data-dec26`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => { if (alive) setDec26Data(d || {}); })
      .catch(() => { if (alive) setDec26Data({}); });
    return () => { alive = false; };
  }, [airline]);

  const tab = tabs.find((t) => t.id === activeTab);
  const cats = (dec26Data && dec26Data[activeTab]) || [];

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className={`absolute inset-0 ${isDark ? "bg-black/70" : "bg-black/40"} backdrop-blur-sm`} />
      <div
        className={`relative w-full max-w-7xl rounded-2xl overflow-hidden flex flex-col ${
          isDark ? "bg-[#0b0b0d] border border-white/10" : "bg-white border border-black/10 shadow-2xl"
        }`}
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? "border-white/10" : "border-black/10"}`}>
          <div className="flex items-center gap-3">
            {activeCat && (
              <button
                onClick={() => setActiveCat(null)}
                data-testid="dec26-back-btn"
                className={`p-2 rounded-lg ${isDark ? "hover:bg-white/10 text-slate-400" : "hover:bg-black/[0.05] text-slate-500"}`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND}20` }}>
              <BookOpenCheck className="w-5 h-5" style={{ color: BRAND }} />
            </div>
            <div>
              <div className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                {activeCat ? activeCat.name : "Sample Dec'26 Proposal"}
              </div>
              <div className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {activeCat
                  ? `${activeCat.titles.length} titles · ${tab.label}`
                  : `Curated Movies, TV and Audio for ${airline?.name || ""}`}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="dec26-close-btn"
            className={`p-2 rounded-lg ${isDark ? "hover:bg-white/10 text-slate-400" : "hover:bg-black/[0.05] text-slate-500"}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs (hidden in drill-down to focus on detail) */}
        {!activeCat && (
          <div className={`flex items-center gap-1 px-4 pt-3 pb-3 border-b ${isDark ? "border-white/10" : "border-black/10"} overflow-x-auto`}>
            {tabs.map((t) => {
              const Icon = t.icon;
              const isOn = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  data-testid={`dec26-tab-${t.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${
                    isOn ? "" : isDark ? "text-slate-400 hover:bg-white/[0.06]" : "text-slate-500 hover:bg-black/[0.05]"
                  }`}
                  style={
                    isOn
                      ? { backgroundColor: `${t.color}1F`, color: t.color, border: `1px solid ${t.color}55` }
                      : { border: "1px solid transparent" }
                  }
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: isOn ? `${t.color}25` : isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)" }}
                  >
                    {((dec26Data && dec26Data[t.id]) || []).length}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {dec26Data === null ? (
            <div className={`px-8 py-20 text-center text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Loading sample library…
            </div>
          ) : !activeCat ? (
            // LIBRARY VIEW — horizontal scrollable big cards
            <div className="px-8 py-7">
              <div
                className="flex gap-6 overflow-x-auto pb-5 -mx-2 px-2"
                style={{ scrollSnapType: "x mandatory" }}
                data-testid="dec26-cat-scroller"
              >
                {cats.map((cat, i) => {
                  const Icon = iconFor(cat.name);
                  return (
                    <button
                      key={i}
                      onClick={() => setActiveCat(cat)}
                      data-testid={`dec26-cat-${i}`}
                      className={`flex-shrink-0 w-72 text-left rounded-2xl p-6 border-2 transition-all hover:scale-[1.02] flex flex-col ${
                        isDark ? "bg-white/[0.03] hover:border-opacity-80" : "bg-black/[0.02] hover:border-opacity-80"
                      }`}
                      style={{ borderColor: `${tab.color}40`, scrollSnapAlign: "start" }}
                    >
                      <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
                        style={{ backgroundColor: `${tab.color}1F`, border: `1.5px solid ${tab.color}55` }}
                      >
                        <Icon className="w-8 h-8" style={{ color: tab.color }} />
                      </div>
                      <div className={`text-lg font-bold leading-tight mb-3 ${isDark ? "text-white" : "text-slate-900"}`}>
                        {cat.name}
                      </div>
                      <div className={`text-sm leading-snug mb-5 line-clamp-4 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        {cat.rationale || "Curated selection for our passengers."}
                      </div>
                      <div className="mt-auto flex items-center justify-between">
                        <span
                          className="text-xs font-bold px-2.5 py-1 rounded-full"
                          style={{ backgroundColor: `${tab.color}1F`, color: tab.color }}
                        >
                          {cat.titles.length} titles
                        </span>
                        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: tab.color }}>
                          Open →
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className={`mt-2 text-xs italic ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                Scroll horizontally to browse all categories · Click a card to view full title list and rationale.
              </div>
            </div>
          ) : (
            // DRILL-DOWN VIEW — rationale + full title table
            <div className="p-6">
              {/* Rationale */}
              {activeCat.rationale && (
                <div
                  className={`rounded-xl p-4 mb-5 border ${
                    isDark ? "bg-white/[0.03] border-white/10" : "bg-amber-50 border-amber-200"
                  }`}
                >
                  <div className="text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: tab.color }}>
                    Category Rationale
                  </div>
                  <p className={`text-sm leading-relaxed ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                    {activeCat.rationale}
                  </p>
                </div>
              )}

              {/* Titles table */}
              <TitleTable titles={activeCat.titles} tabId={activeTab} isDark={isDark} accent={tab.color} />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const tabColumns = {
  hollywood: [
    { key: "title", label: "Title", width: "w-[22%]", bold: true },
    { key: "studio", label: "Studio", width: "w-[10%]" },
    { key: "genre", label: "Genre", width: "w-[12%]" },
    { key: "cast", label: "Cast", width: "w-[18%]", italic: true },
    { key: "year", label: "Year", width: "w-[6%]" },
    { key: "imdb", label: "IMDb", width: "w-[6%]", pill: true },
    { key: "synopsis", label: "Synopsis", width: "w-[26%]" },
  ],
  intl: [
    { key: "title", label: "Title", width: "w-[22%]", bold: true },
    { key: "genre", label: "Genre", width: "w-[13%]" },
    { key: "cast", label: "Cast", width: "w-[20%]", italic: true },
    { key: "year", label: "Year", width: "w-[6%]" },
    { key: "imdb", label: "IMDb", width: "w-[6%]", pill: true },
    { key: "synopsis", label: "Synopsis", width: "w-[33%]" },
  ],
  short: [
    { key: "title", label: "Title", width: "w-[22%]", bold: true },
    { key: "season", label: "Season", width: "w-[10%]" },
    { key: "genre", label: "Genre", width: "w-[10%]" },
    { key: "cast", label: "Cast", width: "w-[20%]", italic: true },
    { key: "year", label: "Year", width: "w-[6%]" },
    { key: "synopsis", label: "Synopsis", width: "w-[32%]" },
  ],
  audio: [
    { key: "title", label: "Title", width: "w-[40%]", bold: true },
    { key: "artist", label: "Artist", width: "w-[30%]" },
    { key: "year", label: "Year", width: "w-[10%]" },
    { key: "genre", label: "Genre", width: "w-[20%]" },
  ],
};

const TitleTable = ({ titles, tabId, isDark, accent }) => {
  const cols = tabColumns[tabId] || tabColumns.hollywood;
  return (
    <div className={`rounded-xl border overflow-hidden ${isDark ? "border-white/10" : "border-black/10"}`}>
      <table className="w-full text-sm" data-testid="dec26-titles-table">
        <thead>
          <tr
            className={`text-left ${
              isDark ? "bg-white/[0.06] text-slate-300" : "bg-black/[0.04] text-slate-600"
            }`}
            style={{ borderBottom: `2px solid ${accent}55` }}
          >
            {cols.map((c) => (
              <th
                key={c.key}
                className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider ${c.width}`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {titles.map((t, i) => (
            <tr
              key={i}
              className={`${
                isDark
                  ? i % 2 === 0
                    ? "bg-white/[0.03]"
                    : "bg-white/[0.02]"
                  : i % 2 === 0
                  ? "bg-white"
                  : "bg-black/[0.02]"
              } ${isDark ? "hover:bg-white/10/40" : "hover:bg-amber-50"}`}
            >
              {cols.map((c) => {
                const raw = t[c.key];
                const value = raw == null || raw === "" ? "—" : raw;
                if (c.pill && raw && raw !== "-") {
                  return (
                    <td key={c.key} className={`px-3 py-2.5 align-top`}>
                      <span
                        className="inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${accent}1F`, color: accent }}
                      >
                        <Star className="w-2.5 h-2.5" fill="currentColor" />
                        {raw}
                      </span>
                    </td>
                  );
                }
                return (
                  <td
                    key={c.key}
                    className={`px-3 py-2.5 align-top leading-snug ${
                      c.bold ? "font-bold" : ""
                    } ${c.italic ? "italic" : ""} ${
                      isDark ? "text-slate-200" : "text-slate-700"
                    } ${value === "—" ? (isDark ? "text-slate-600" : "text-slate-400") : ""}`}
                  >
                    {value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Dec26Modal;
